import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  HandCoins, Calendar, Euro, ExternalLink, Loader2, Search,
  Clock, AlertTriangle, CheckCircle, TrendingUp, FileText,
  Target, Lightbulb, Building2, ChevronRight, Globe, Info,
} from "lucide-react";
import { format, isPast, isFuture, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";

const API = process.env.REACT_APP_BACKEND_URL;

function getStatusInfo(grant) {
  const deadline = grant.deadline ? new Date(grant.deadline) : null;
  const isPastDeadline = deadline && isPast(deadline);
  const daysLeft = deadline && !isPastDeadline ? differenceInDays(deadline, new Date()) : null;
  return { deadline, isPastDeadline, daysLeft };
}

function StatusBadge({ grant }) {
  const { isPastDeadline, daysLeft } = getStatusInfo(grant);
  if (isPastDeadline) return <Badge variant="secondary" className="text-xs">Cerrada</Badge>;
  if (daysLeft !== null && daysLeft <= 7) return <Badge className="bg-red-500 text-white text-xs">Quedan {daysLeft} dias</Badge>;
  if (daysLeft !== null && daysLeft <= 30) return <Badge className="bg-amber-500 text-white text-xs">Quedan {daysLeft} dias</Badge>;
  if (grant.deadline) return <Badge className="bg-green-100 text-green-700 text-xs">Abierta</Badge>;
  return null;
}

function FundingDisplay({ grant }) {
  if (!grant.funding_min && !grant.funding_max) return null;
  const fmt = (n) => n >= 1000000 ? (n / 1000000).toFixed(1) + "M" : (n / 1000).toFixed(0) + "K";
  return (
    <div className="flex items-center gap-1.5 text-emerald-700 font-semibold">
      <Euro className="w-4 h-4" />
      {grant.funding_min && grant.funding_max
        ? `${fmt(grant.funding_min)} - ${fmt(grant.funding_max)}`
        : grant.funding_max ? `Hasta ${fmt(grant.funding_max)}` : `Desde ${fmt(grant.funding_min)}`}
    </div>
  );
}

function DetailModal({ grant, open, onClose }) {
  if (!grant) return null;
  const { deadline, isPastDeadline, daysLeft } = getStatusInfo(grant);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto embed-light">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isPastDeadline ? "bg-gray-100" : "bg-blue-100"}`}>
              <HandCoins className={`w-6 h-6 ${isPastDeadline ? "text-gray-400" : "text-blue-600"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl leading-tight">{grant.name}</DialogTitle>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <StatusBadge grant={grant} />
                {grant.agency && <Badge variant="outline" className="text-xs">{grant.agency}</Badge>}
                {grant.program && <Badge className="bg-blue-50 text-blue-700 text-xs">{grant.program}</Badge>}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Key info cards */}
          <div className="grid grid-cols-2 gap-3">
            {deadline && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-500 mb-1"><Calendar className="w-4 h-4" /><span className="text-xs">Fecha limite</span></div>
                <p className="text-sm font-semibold">{format(deadline, "d MMMM yyyy", { locale: es })}</p>
                {daysLeft !== null && <p className="text-xs text-amber-600 mt-0.5">{daysLeft} dias restantes</p>}
              </div>
            )}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1"><Euro className="w-4 h-4" /><span className="text-xs">Financiacion</span></div>
              <FundingDisplay grant={grant} />
              {!grant.funding_min && !grant.funding_max && <p className="text-sm text-gray-400">No especificada</p>}
            </div>
          </div>

          {/* Description - Para que sirve */}
          {grant.description && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-semibold text-gray-800">Para que sirve esta subvencion</p>
              </div>
              <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap bg-blue-50/50 p-4 rounded-lg">{grant.description}</p>
            </div>
          )}

          {/* Requirements - Que se prioriza */}
          {grant.requirements && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-semibold text-gray-800">Proyectos y tematicas priorizados</p>
              </div>
              <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap bg-amber-50/50 p-4 rounded-lg">{grant.requirements}</p>
            </div>
          )}

          {/* Organismo */}
          {(grant.agency || grant.program) && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-purple-600" />
                <p className="text-sm font-semibold text-gray-800">Organismo y programa</p>
              </div>
              <div className="bg-purple-50/50 p-4 rounded-lg space-y-1">
                {grant.agency && <p className="text-sm"><span className="text-gray-500">Organismo:</span> <strong>{grant.agency}</strong></p>}
                {grant.program && <p className="text-sm"><span className="text-gray-500">Programa:</span> <strong>{grant.program}</strong></p>}
              </div>
            </div>
          )}

          {/* Link */}
          {grant.url ? (
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" asChild>
              <a href={grant.url} target="_blank" rel="noopener noreferrer"><Globe className="w-4 h-4 mr-2" />Ver convocatoria oficial</a>
            </Button>
          ) : (
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>No hay enlace disponible. Busca "<strong>{grant.name}</strong>" en la web del organismo <strong>{grant.agency || "correspondiente"}</strong> para mas informacion.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GrantCard({ grant, onClick }) {
  const { deadline, isPastDeadline, daysLeft } = getStatusInfo(grant);
  return (
    <Card className={`hover:shadow-md transition-all cursor-pointer group ${isPastDeadline ? "opacity-60 hover:opacity-100" : ""}`} onClick={onClick} data-testid={`grant-embed-${grant.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base leading-tight group-hover:text-blue-700 transition-colors">{grant.name}</CardTitle>
          <StatusBadge grant={grant} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {grant.description && <p className="text-sm text-gray-600 line-clamp-2">{grant.description}</p>}
        <div className="flex flex-wrap gap-2 text-xs">
          {deadline && (
            <span className="flex items-center gap-1 text-gray-500">
              <Calendar className="w-3 h-3" />{format(deadline, "d MMM yyyy", { locale: es })}
            </span>
          )}
          {grant.agency && <Badge variant="outline" className="text-xs">{grant.agency}</Badge>}
          {grant.program && <Badge variant="outline" className="text-xs bg-blue-50">{grant.program}</Badge>}
        </div>
        <FundingDisplay grant={grant} />
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center text-xs text-gray-400 group-hover:text-blue-600 transition-colors">
            <ChevronRight className="w-3.5 h-3.5 mr-1" />Ver detalle completo
          </div>
          {grant.url && (
            <a href={grant.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700" onClick={e => e.stopPropagation()}>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SubvencionesEmbedPage() {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPast, setShowPast] = useState(false);
  const [agencyFilter, setAgencyFilter] = useState("all");
  const [detailGrant, setDetailGrant] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetch(API + "/api/public/grant-calls")
      .then(r => r.ok ? r.json() : [])
      .then(data => setGrants(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const agencies = [...new Set(grants.map(g => g.agency).filter(Boolean))].sort();

  const filtered = grants
    .filter(g => {
      const matchSearch = !searchTerm ||
        (g.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (g.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (g.agency || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (g.requirements || "").toLowerCase().includes(searchTerm.toLowerCase());
      const pastDeadline = g.deadline && isPast(new Date(g.deadline));
      const matchAgency = agencyFilter === "all" || g.agency === agencyFilter;
      return matchSearch && (showPast || !pastDeadline) && matchAgency;
    })
    .sort((a, b) => {
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline) - new Date(b.deadline);
    });

  const active = grants.filter(g => g.deadline && isFuture(new Date(g.deadline))).length;
  const closingSoon = grants.filter(g => {
    if (!g.deadline) return false;
    const d = new Date(g.deadline);
    return isFuture(d) && differenceInDays(d, new Date()) <= 30;
  }).length;
  const totalFunding = grants.reduce((sum, g) => sum + (g.funding_max || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-screen bg-gray-50 embed-light"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 embed-light" data-testid="subvenciones-embed">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2"><HandCoins className="w-8 h-8" /><h1 className="text-2xl font-bold">Convocatorias y Subvenciones</h1></div>
          <p className="text-blue-100 text-sm">Valoriza - Oportunidades de Financiacion I+D+i</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <HandCoins className="w-5 h-5 mx-auto mb-1 text-blue-600" /><p className="text-2xl font-bold">{grants.length}</p><p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-600" /><p className="text-2xl font-bold">{active}</p><p className="text-xs text-gray-500">Activas</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-amber-600" /><p className="text-2xl font-bold">{closingSoon}</p><p className="text-xs text-gray-500">Cierran pronto</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm border">
            <Euro className="w-5 h-5 mx-auto mb-1 text-emerald-600" /><p className="text-2xl font-bold">{totalFunding >= 1000000 ? (totalFunding / 1000000).toFixed(1) + "M" : (totalFunding / 1000).toFixed(0) + "K"}</p><p className="text-xs text-gray-500">Financiacion total</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Buscar por nombre, descripcion, requisitos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white" />
          </div>
          {agencies.length > 1 && (
            <Select value={agencyFilter} onValueChange={setAgencyFilter}>
              <SelectTrigger className="w-[200px] bg-white"><SelectValue placeholder="Organismo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los organismos</SelectItem>
                {agencies.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={showPast} onChange={e => setShowPast(e.target.checked)} className="rounded" />
            Mostrar cerradas
          </label>
        </div>

        <p className="text-xs text-gray-400">{filtered.length} convocatorias{agencyFilter !== "all" ? ` de ${agencyFilter}` : ""}</p>

        {/* Tip */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700 border border-blue-100">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>Haz clic en cualquier convocatoria para ver su <strong>descripcion completa</strong>, <strong>requisitos</strong>, <strong>tematicas priorizadas</strong> y enlace a la fuente oficial.</p>
        </div>

        {/* Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((grant, i) => (
            <GrantCard key={grant.id || i} grant={grant} onClick={() => { setDetailGrant(grant); setDetailOpen(true); }} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <HandCoins className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No se encontraron subvenciones</p>
          </div>
        )}

        <DetailModal grant={detailGrant} open={detailOpen} onClose={setDetailOpen} />

        <div className="text-center text-xs text-gray-400 pt-4">Innovation OS - Valoriza</div>
      </div>
    </div>
  );
}
